import {
  CollectionName,
  CollectionVisibility,
} from "../registry/collection-registry"
import type { CollectionCreateSchema } from "../types/typesense"

export const transferRequestsSchema: CollectionCreateSchema = {
  name: CollectionName.TransferRequests,
  enable_nested_fields: true,
  fields: [
    {
      name: "id",
      type: "string",
      sort: true,
    },
    {
      name: "status",
      type: "string",
      facet: true,
      sort: true,
      infix: true,
    },
    {
      name: "certificate_number",
      type: "string",
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "certificate_number_sort",
      type: "string",
      optional: true,
      sort: true,
    },
    {
      name: "consignor_id",
      type: "string",
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "consignee_id",
      type: "string",
      facet: true,
      sort: true,
      infix: true,
      optional: true,
    },
    {
      name: "third_party_consignor",
      type: "string[]",
      infix: true,
      facet: true,
      optional: true,
    },
    {
      name: "third_party_consignor_count",
      type: "int32",
      optional: true,
      index: false,
    },
    {
      name: "departure_date",
      type: "string",
      optional: true,
      infix: false,
      sort: true,
      index: true,
    },
    {
      name: "departure_ts",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
      index: true,
    },
    {
      name: "updated_at",
      type: "string",
      optional: true,
      sort: true,
      infix: false,
      index: true,
    },
    {
      name: "accessible_by",
      type: "string[]",
      optional: true,
    },
    {
      name: "unofficial_information",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "submitted_at",
      type: "string",
      optional: true,
      sort: true,
      infix: false,
      index: true,
    },
    {
      name: "approved_at",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
    },
    {
      name: "unofficial_commercial_info",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "unique_consignment_ref",
      type: "string",
      infix: true,
      optional: true,
    },
    {
      name: "product",
      type: "object[]",
      infix: true,
      optional: true,
      fields: [
        {
          name: "id",
          type: "string",
          index: false,
        },
        {
          name: "description",
          type: "string",
          infix: true,
        },
      ],
    },
    {
      name: "product_descriptions",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_net_weights",
      type: "string[]",
      optional: true,
      index: false,
    },
    {
      name: "product_net_volumes",
      type: "string[]",
      optional: true,
      index: false,
    },
    {
      name: "product_eligibility_countries",
      type: "string[]",
      optional: true,
      index: false,
    },
    {
      name: "product_container_numbers",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_seals",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_NZHSC_codes",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_productionBatch_ids",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "product_item_marks",
      type: "string[]",
      infix: true,
      optional: true,
    },
    {
      name: "product_unofficial_commercial_ref",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "product_unofficial_commercial_info",
      type: "string[]",
      optional: true,
      infix: true,
    },
    {
      name: "local_carrier",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "carrier_name",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "conveyance_reference",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "destination_country",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "transport_mode",
      type: "string",
      optional: true,
      facet: true,
      infix: true,
      sort: true,
    },
    {
      name: "org_cert_ref_code",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "vessel",
      type: "string",
      optional: true,
      sort: true,
      infix: true,
    },
    {
      name: "sub_class",
      type: "string[]",
      optional: true,
      infix: true,
      facet: true,
    },
    {
      name: "class",
      type: "string[]",
      optional: true,
      infix: true,
      facet: true,
    },
    {
      name: "requires_attention",
      type: "bool",
      optional: true,
      index: false,
    },
    {
      name: "updated_ts",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
      index: true,
    },
    {
      name: "submitted_ts",
      type: "int64",
      optional: true,
      facet: true,
      sort: true,
      index: true,
    },
    {
      name: "status_label",
      type: "string",
      optional: true,
      facet: true,
      infix: false,
      sort: true,
      index: true,
    },
    {
      name: "urgency",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "eligibility_declaration",
      type: "bool",
      optional: true,
      index: false,
    },
    {
      name: "template_code",
      type: "string",
      optional: true,
      infix: true,
      sort: true,
    },
    {
      name: "template_version",
      type: "string",
      optional: true,
      index: false,
    },
    {
      name: "is_exhausted",
      type: "bool",
      optional: true,
      facet: true,
    },
  ],
  visibility: CollectionVisibility.Private,
}
